import type { VesselPreNominationRequestPayload } from "@services/vessel-nomination/types";
import { postNomination, putNomination } from "@services/vesselNominationApi";
import { useMutation } from "@tanstack/react-query";
import mapPreNominationRequestToPayload from "@utils/mappers/vesselPreNominationMapper";
import type { PreNominationRequestFormData } from "../constants";
import type {
  getPreNominationRequestSchema,
  PreNominationValidatedFormData,
} from "../schema/preNominationRequestSchema";

interface UseVesselPreNominationProps {
  formValues?: PreNominationRequestFormData;
  schema?: ReturnType<typeof getPreNominationRequestSchema>;
  onSuccess?: () => void;
  onError?: () => void;
  isResubmitting?: boolean;
  nominationId?: string;
}

interface UseVesselPreNominationResult {
  onSubmit: () => Promise<void>;
  isSubmitPending: boolean;
}

export const useVesselPreNomination = ({
  formValues,
  schema,
  onSuccess,
  onError,
  isResubmitting,
  nominationId,
}: UseVesselPreNominationProps): UseVesselPreNominationResult => {
  const addPreNomination = useMutation<
    VesselPreNominationRequestPayload,
    unknown,
    { payload: VesselPreNominationRequestPayload }
  >({
    mutationFn: ({ payload }) =>
      isResubmitting && nominationId
        ? putNomination({ nominationPayload: payload, nominationId: nominationId })
        : postNomination({ nominationPayload: payload }),
    onSuccess,
    onError,
  });

  const onSubmit = async (): Promise<void> => {
    if (!formValues || !schema) return;

    const validatedFormValues: PreNominationValidatedFormData = schema.parse(formValues);
    const preNominationPayload = mapPreNominationRequestToPayload(validatedFormValues);

    addPreNomination.mutate({ payload: preNominationPayload });
  };

  return {
    onSubmit,
    isSubmitPending: addPreNomination.isPending,
  };
};
